import { extent, merge, range } from 'd3-array';
import { polygonArea, polygonCentroid, polygonContains } from 'd3-polygon';

import { pointDistanceSquared } from './pointDistanceSquared.js';
import { polygonInside } from './polygonInside.js';
import { polygonRayCast } from './polygonRayCast.js';
import { polygonRotate } from './polygonRotate.js';
import { simplify } from './simplify.js';
import { Point, Rectangle } from './types.js';

// Algorithm constants
const aspectRatioStep = 0.5; // step size for the aspect ratio
const angleStep = 5; // step size for angles (in degrees); has linear impact on running time

const polyCache: Record<string, any> = {};

/**
 * An Object that allows for overriding various parameters of the largestRect algorithm.
 */
export interface Options {
  /**
   * The allowed rotations of the final rectangle.
   * An angle of zero means that the longer side of the polygon (the width) will be aligned with the x axis. An angle of 90 and/or -90 means that the longer side of the polygon (the width) will be aligned with the y axis. The value can be a number between -90 and 90 specifying the angle of rotation of the polygon, a string which is parsed to a number, or an array of numbers specifying the possible rotations of the polygon.
   *
   * default: d3.range(-90, 95, 5)
   */
  angle: number | number[];

  /**
   * The ratio between the width and height of the rectangle. The value can be a number, a string which is parsed to a number, or an array of numbers specifying the possible aspect ratios of the final rectangle.
   */
  aspectRatio: number | number[] | undefined;

  /**
   * The maximum aspect ratio (width/height) allowed for the rectangle. This property should only be used if the aspectRatio is not provided.
   * default: 15
   */
  maxAspectRatio: number;

  /**
   * The minimum aspect ratio (width/height) allowed for the rectangle. This property should only be used if the aspectRatio is not provided.
   * default: 1
   */
  minAspectRatio: number;

  /**
   * The number of randomly drawn points inside the polygon which the algorithm explores as possible center points of the maximal rectangle.
   * default: 20
   */
  nTries: number;

  /**
   * The minimum height of the rectangle.
   * default: 0
   */
  minHeight: number;

  /**
   * The minimum width of the rectangle.
   * default: 0
   */
  minWidth: number;

  /**
   * The simplification tolerance factor, between 0 and 1. A larger tolerance corresponds to more extensive simplification.
   * default: 0.02
   */
  tolerance: number;

  /**
   * The center point of the rectangle. If specified, the rectangle will be fixed at that point, otherwise the algorithm optimizes across all possible points. The given value can be either a two dimensional array specifying the x and y coordinate of the origin or an array of two dimensional points specifying multiple possible center points of the rectangle.
   */
  origin: Point | Point[] | undefined;

  /**
   * Whether or not to cache the result, which would be used in subsequent calculations to preserve consistency and speed up calculation time.
   * default: true
   */
  cache: boolean;

  /**
   * Whether to return intermediate step results for debugging
   * default: false
   */
  events: boolean;
}
/**
 * @author Daniel Smilkov [dsmilkov@gmail.com]
 * @see {@link https://github.com/d3plus/d3plus-shape/blob/main/src/geom/largestRect.js}
 * @desc Finds larget rectnagle that can be fit into a given polygon
 * @param poly - An Array of points that represent a polygon.
 * @param opts - An Object that allows for overriding various parameters of the algorithm.
 * @returns {{ maxRect: Rectangle | null; events: any[] | null }}
 */
export const largestRect = (
  poly: Point[],
  opts: Partial<Options> = {}
): { maxRect: Rectangle | null; events: any[] | null } => {
  if (poly.length < 3) {
    console.error('polygon has to have at least 3 points', poly);
    return { maxRect: null, events: null };
  }

  // For visualization debugging purposes
  const events: any[] = [];

  // User's input normalization
  const options: Options = Object.assign(
    {
      angle: range(-90, 90 + angleStep, angleStep),
      aspectRatio: undefined,
      maxAspectRatio: 15,
      minAspectRatio: 1,
      minHeight: 0,
      minWidth: 0,
      nTries: 20,
      tolerance: 0.02,
      origin: undefined,
      cache: true,
      events: false,
    },
    opts
  );

  const angles: number[] =
    options.angle instanceof Array
      ? options.angle
      : typeof options.angle === 'number'
      ? [options.angle]
      : typeof options.angle === 'string' && !isNaN(options.angle)
      ? [Number(options.angle)]
      : [];

  const aspectRatios: number[] =
    options.aspectRatio instanceof Array
      ? options.aspectRatio
      : typeof options.aspectRatio === 'number'
      ? [options.aspectRatio]
      : typeof options.aspectRatio === 'string' && !isNaN(options.aspectRatio)
      ? [Number(options.aspectRatio)]
      : [];

  const origins: Point[] =
    options.origin && options.origin instanceof Array
      ? options.origin[0] instanceof Array
        ? (options.origin as Point[])
        : ([options.origin] as Point[])
      : ([] as Point[]);

  let cacheString: string = '';
  if (options.cache) {
    cacheString = merge(poly).join(',');
    cacheString += `-${options.minAspectRatio}`;
    cacheString += `-${options.maxAspectRatio}`;
    cacheString += `-${options.minHeight}`;
    cacheString += `-${options.minWidth}`;
    cacheString += `-${angles.join(',')}`;
    cacheString += `-${origins.join(',')}`;
    if (polyCache[cacheString]) return polyCache[cacheString];
  }

  const area: number = Math.abs(polygonArea(poly)); // take absolute value of the signed area
  if (area === 0) {
    console.error('polygon has 0 area', poly);
    return { maxRect: null, events: null };
  }
  // get the width of the bounding box of the original polygon to determine tolerance
  let [minx, maxx]: [number, number] = extent(poly, (d) => d[0]) as [number, number];
  let [miny, maxy]: [number, number] = extent(poly, (d) => d[1]) as [number, number];

  // simplify polygon
  const tolerance: number = Math.min(maxx - minx, maxy - miny) * options.tolerance;

  if (tolerance > 0) poly = simplify(poly, tolerance);
  if (options.events) events.push({ type: 'simplify', poly });

  // get the width of the bounding box of the simplified polygon
  [minx, maxx] = extent(poly, (d) => d[0]) as [number, number];
  [miny, maxy] = extent(poly, (d) => d[1]) as [number, number];
  const [boxWidth, boxHeight]: [number, number] = [maxx - minx, maxy - miny];

  // discretize the binary search for optimal width to a resolution of this times the polygon width
  const widthStep: number = Math.min(boxWidth, boxHeight) / 50;

  // populate possible center points with random points inside the polygon
  if (!origins.length) {
    // get the centroid of the polygon
    const centroid: Point = polygonCentroid(poly);
    if (!isFinite(centroid[0])) {
      console.error('cannot find centroid', poly);
      return { maxRect: null, events: null };
    }
    if (polygonContains(poly, centroid)) origins.push(centroid);

    let nTries: number = options.nTries;
    // get few more points inside the polygon
    while (nTries) {
      const rndX: number = Math.random() * boxWidth + minx;
      const rndY: number = Math.random() * boxHeight + miny;
      const rndPoint: Point = [rndX, rndY];
      if (polygonContains(poly, rndPoint)) {
        origins.push(rndPoint);
      }
      nTries--;
    }
  }
  if (options.events) events.push({ type: 'origins', points: origins });
  let maxArea: number = 0;
  let maxRect: Rectangle | null = null;

  for (let ai = 0; ai < angles.length; ai++) {
    const angle: number = angles[ai];
    const angleRad: number = (-angle * Math.PI) / 180;
    if (options.events) events.push({ type: 'angle', angle });
    for (let i = 0; i < origins.length; i++) {
      const origOrigin: Point = origins[i];
      // generate improved origins
      const [p1W, p2W]: [Point | null, Point | null] = polygonRayCast(poly, origOrigin, angleRad);
      const [p1H, p2H]: [Point | null, Point | null] = polygonRayCast(
        poly,
        origOrigin,
        angleRad + Math.PI / 2
      );
      const modifOrigins: Point[] = [];
      if (p1W && p2W) modifOrigins.push([(p1W[0] + p2W[0]) / 2, (p1W[1] + p2W[1]) / 2]); // average along with width axis
      if (p1H && p2H) modifOrigins.push([(p1H[0] + p2H[0]) / 2, (p1H[1] + p2H[1]) / 2]); // average along with height axis

      if (options.events)
        events.push({ type: 'modifOrigin', idx: i, p1W, p2W, p1H, p2H, modifOrigins });

      for (let i = 0; i < modifOrigins.length; i++) {
        const origin: Point = modifOrigins[i];

        if (options.events) events.push({ type: 'origin', cx: origin[0], cy: origin[1] });

        const [p1W, p2W]: [Point | null, Point | null] = polygonRayCast(poly, origin, angleRad);
        if (p1W === null || p2W === null) continue;
        const minSqDistW: number = Math.min(
          pointDistanceSquared(origin, p1W),
          pointDistanceSquared(origin, p2W)
        );
        const maxWidth: number = 2 * Math.sqrt(minSqDistW);

        const [p1H, p2H]: [Point | null, Point | null] = polygonRayCast(
          poly,
          origin,
          angleRad + Math.PI / 2
        );
        if (p1H === null || p2H === null) continue;
        const minSqDistH: number = Math.min(
          pointDistanceSquared(origin, p1H),
          pointDistanceSquared(origin, p2H)
        );
        const maxHeight: number = 2 * Math.sqrt(minSqDistH);

        if (maxWidth * maxHeight < maxArea) continue;

        let aRatios: number[] = aspectRatios;
        if (!aRatios.length) {
          const minAspectRatio: number = Math.max(
            options.minAspectRatio,
            options.minWidth / maxHeight,
            maxArea / (maxHeight * maxHeight)
          );
          const maxAspectRatio: number = Math.min(
            options.maxAspectRatio,
            maxWidth / options.minHeight,
            (maxWidth * maxWidth) / maxArea
          );
          aRatios = range(minAspectRatio, maxAspectRatio + aspectRatioStep, aspectRatioStep);
        }

        for (let a = 0; a < aRatios.length; a++) {
          const aRatio: number = aRatios[a];

          // do a binary search to find the max width that works
          let left: number = Math.max(options.minWidth, Math.sqrt(maxArea * aRatio));
          let right: number = Math.min(maxWidth, maxHeight * aRatio);
          if (right * maxHeight < maxArea) continue;

          if (options.events && right - left >= widthStep) events.push({ type: 'aRatio', aRatio });

          while (right - left >= widthStep) {
            const width: number = (left + right) / 2;
            const height: number = width / aRatio;
            const [cx, cy]: Point = origin;
            let rectPoly: Point[] = [
              [cx - width / 2, cy - height / 2],
              [cx + width / 2, cy - height / 2],
              [cx + width / 2, cy + height / 2],
              [cx - width / 2, cy + height / 2],
            ];
            rectPoly = polygonRotate(rectPoly, angleRad, origin);
            const insidePoly: boolean = polygonInside(rectPoly, poly);
            if (insidePoly) {
              // we know that the area is already greater than the maxArea found so far
              maxArea = width * height;
              rectPoly.push(rectPoly[0]);
              maxRect = { area: maxArea, cx, cy, width, height, angle: -angle, points: rectPoly };
              left = width; // increase the width in the binary search
            } else {
              right = width; // decrease the width in the binary search
            }
            if (options.events)
              events.push({
                type: 'rectangle',
                areaFraction: (width * height) / area,
                cx,
                cy,
                width,
                height,
                angle,
                insidePoly,
              });
          }
        }
      }
    }
  }

  if (options.cache) {
    polyCache[cacheString] = {
      maxRect,
      events: options.events ? events : null,
    };
  }

  return {
    maxRect,
    events: options.events ? events : null,
  };
};
